/**
 * HudDomAdapter — Phase 2+3: battle HUD + overlays in DOM.
 * Phase 2 sections: top-bar, left-strip, right-column, wave-cards.
 * Phase 3 overlays: settings-panel, tutorial-overlay, summary-overlay,
 *                   skills-panel, stats-panel, map-overlay.
 */

import type { HudDomPort, GameStateView } from "../../application/ports.js";
import { activeCampaign } from "../../campaign/index.js";
import { createTopBar, type TopBarCallbacks } from "./top-bar.js";
import { createLeftStrip, type LeftStripCallbacks } from "./left-strip.js";
import { createRightColumn, type RightColumnCallbacks } from "./right-column.js";
import { createWaveCards } from "./wave-cards.js";
import { createSettingsPanel, type SettingsPanelCallbacks } from "./settings-panel.js";
import { createTutorialOverlay, type TutorialOverlayCallbacks } from "./tutorial-overlay.js";
import { createSummaryOverlay, type SummaryOverlayCallbacks } from "./summary-overlay.js";
import { createSkillsPanel, type SkillsPanelCallbacks } from "./skills-panel.js";
import { createStatsPanel, type StatsPanelCallbacks } from "./stats-panel.js";
import { createMapOverlay, type MapOverlayCallbacks } from "./map-overlay.js";
import { createEditorPanel, type EditorPanelCallbacks } from "./editor-panel.js";
import { createBattleMenuOverlay, type BattleMenuCallbacks } from "./battle-menu-overlay.js";

export interface HudDomAdapterOptions {
  mountTarget: HTMLElement;
  // Phase 2 callbacks
  onSpeedChange?: (speed: 0 | 1 | 3) => void;
  onPlacementModeChange?: (mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine") => void;
  onWaveSummon?: (waveIndex: number) => void;
  onGemCreate?: (gemType: string) => void;
  onSanctify?: () => void;
  onReplicate?: () => void;
  onDuplicate?: () => void;
  onCombine?: () => void;
  onMeltdown?: () => void;
  onInventoryDragStart?: (params: { gemId: string; screenX: number; screenY: number }) => void;
  onInventoryGemClick?: (gemId: string) => void;
  onInventoryGemDrop?: (sourceGemId: string, targetGemId: string) => void;
  onSettings?: () => void;
  // Phase 3 callbacks
  onHpChange?: (index: number) => void;
  onWavesChange?: (index: number) => void;
  onToggleChange?: (key: "extraBanishCost" | "healOnReturn") => void;
  onSettingsStart?: () => void;
  onSettingsCancel?: () => void;
  onTutorialNext?: () => void;
  onTutorialSkip?: () => void;
  onReturnToMap?: () => void;
  onSkillUpgrade?: (skillId: number, options?: { all?: boolean }) => void;
  onSkillRefund?: (skillId: number, options?: { all?: boolean }) => void;
  onSkillsPanelClose?: () => void;
  onSkillsPanelToggle?: () => void;
  onStatsPanelClose?: () => void;
  onStatsPanelToggle?: () => void;
  onFieldLaunch?: (fieldId: string) => void;
  onFieldEdit?: (fieldId: string) => void;
  // Editor panel callbacks
  onEditorSave?: () => void;
  onEditorCancel?: () => void;
  onEditorResetOverride?: () => void;
  onEditorClearWaypoints?: () => void;
  onEditorRemoveLastWaypoint?: () => void;
  onEditorSetMode?: (mode: "waypoint" | "block") => void;
  onEditorSetWavesNum?: (n: number) => void;
  onEditorSetMonstersPerWave?: (n: number) => void;
  onEditorSetMonsterBaseHp?: (n: number) => void;
  onEditorExportYaml?: () => void;
  // In-battle settings menu (opened by top-bar ⚙ during battle)
  onBattleMenuOpen?: () => void;
  onBattleMenuClose?: () => void;
  onBattleMenuToggleAudio?: () => void;
  onBattleMenuToggleLocale?: () => void;
  onBattleMenuExit?: () => void;
  onBattleMenuRestart?: () => void;
  isAudioMuted?: () => boolean;
}

export function createHudDomAdapter(opts: HudDomAdapterOptions): HudDomPort {
  let container: HTMLDivElement | null = null;
  let battleWrap: HTMLDivElement | null = null;

  let topBar: ReturnType<typeof createTopBar> | null = null;
  let leftStrip: ReturnType<typeof createLeftStrip> | null = null;
  let rightColumn: ReturnType<typeof createRightColumn> | null = null;
  let waveCards: ReturnType<typeof createWaveCards> | null = null;
  let settingsPanel: ReturnType<typeof createSettingsPanel> | null = null;
  let tutorialOverlay: ReturnType<typeof createTutorialOverlay> | null = null;
  let summaryOverlay: ReturnType<typeof createSummaryOverlay> | null = null;
  let skillsPanel: ReturnType<typeof createSkillsPanel> | null = null;
  let statsPanel: ReturnType<typeof createStatsPanel> | null = null;
  let mapOverlay: ReturnType<typeof createMapOverlay> | null = null;
  let editorPanel: ReturnType<typeof createEditorPanel> | null = null;
  let battleMenu: ReturnType<typeof createBattleMenuOverlay> | null = null;

  const topBarCbs: TopBarCallbacks = {
    onSettings: () => {
      // Open the in-battle settings menu instead of the legacy stub callback.
      if (battleMenu) battleMenu.open();
      opts.onSettings?.();
    },
  };

  const leftStripCbs: LeftStripCallbacks = {
    onSpeedChange: opts.onSpeedChange,
    onWaveSummon: opts.onWaveSummon,
  };

  const rightColumnCbs: RightColumnCallbacks = {
    onPlacementModeChange: opts.onPlacementModeChange,
    onGemCreate: opts.onGemCreate,
    onSanctify: opts.onSanctify,
    onReplicate: opts.onReplicate,
    onDuplicate: opts.onDuplicate,
    onCombine: opts.onCombine,
    onMeltdown: opts.onMeltdown,
    onInventoryDragStart: opts.onInventoryDragStart,
    onInventoryGemClick: opts.onInventoryGemClick,
    onInventoryGemDrop: opts.onInventoryGemDrop,
  };

  const settingsCbs: SettingsPanelCallbacks = {
    onHpChange: opts.onHpChange,
    onWavesChange: opts.onWavesChange,
    onToggleChange: opts.onToggleChange,
    onStart: opts.onSettingsStart,
    onCancel: opts.onSettingsCancel,
  };

  const tutorialCbs: TutorialOverlayCallbacks = {
    onNext: opts.onTutorialNext,
    onSkip: opts.onTutorialSkip,
  };

  const summaryCbs: SummaryOverlayCallbacks = {
    onReturnToMap: opts.onReturnToMap,
  };

  const skillsCbs: SkillsPanelCallbacks = {
    onSkillUpgrade: opts.onSkillUpgrade,
    onSkillRefund: opts.onSkillRefund,
    onClose: opts.onSkillsPanelClose,
    onToggle: opts.onSkillsPanelToggle,
  };

  const statsCbs: StatsPanelCallbacks = {
    onClose: opts.onStatsPanelClose,
    onToggle: opts.onStatsPanelToggle,
  };

  return {
    mount(parent: HTMLElement): void {
      if (container) return; // already mounted
      container = document.createElement("div");
      container.className = "hud";
      if (activeCampaign?.ui?.theme) {
        container.dataset.theme = activeCampaign.ui.theme;
      }
      parent.appendChild(container);

      // Top bar is shared across battle + map scenes
      topBar = createTopBar(container, topBarCbs);

      // Phase 2: battle HUD sections — wrapped so we can hide them on non-battle scenes
      battleWrap = document.createElement("div");
      battleWrap.className = "hud__battle";
      container.appendChild(battleWrap);
      leftStrip = createLeftStrip(battleWrap, leftStripCbs);
      rightColumn = createRightColumn(battleWrap, rightColumnCbs);
      waveCards = createWaveCards(battleWrap);

      // Phase 3: overlays
      settingsPanel = createSettingsPanel(container, settingsCbs);
      tutorialOverlay = createTutorialOverlay(container, tutorialCbs);
      summaryOverlay = createSummaryOverlay(container, summaryCbs);

      const battleMenuCbs: BattleMenuCallbacks = {
        onOpen: opts.onBattleMenuOpen,
        onClose: opts.onBattleMenuClose,
        onToggleAudio: opts.onBattleMenuToggleAudio,
        onToggleLocale: opts.onBattleMenuToggleLocale,
        onExitToMap: opts.onBattleMenuExit,
        onRestart: opts.onBattleMenuRestart,
        isAudioMuted: opts.isAudioMuted,
      };
      battleMenu = createBattleMenuOverlay(container, battleMenuCbs);

      // Map overlay (XP widget + map-buttons container + bottom CTA bar)
      const mapOverlayCbs: MapOverlayCallbacks = {
        onFieldLaunch: opts.onFieldLaunch,
        onFieldEdit: opts.onFieldEdit,
      };
      mapOverlay = createMapOverlay(container, mapOverlayCbs);

      // Editor panel (right-side overlay for editor scene)
      const editorCbs: EditorPanelCallbacks = {
        onSave: opts.onEditorSave,
        onCancel: opts.onEditorCancel,
        onResetOverride: opts.onEditorResetOverride,
        onClearWaypoints: opts.onEditorClearWaypoints,
        onRemoveLastWaypoint: opts.onEditorRemoveLastWaypoint,
        onSetMode: opts.onEditorSetMode,
        onSetWavesNum: opts.onEditorSetWavesNum,
        onSetMonstersPerWave: opts.onEditorSetMonstersPerWave,
        onSetMonsterBaseHp: opts.onEditorSetMonsterBaseHp,
        onExportYaml: opts.onEditorExportYaml,
      };
      editorPanel = createEditorPanel(container, editorCbs);

      // Skills and stats panels share the mapButtonsContainer
      skillsPanel = createSkillsPanel(container, mapOverlay.mapButtonsContainer, skillsCbs);
      statsPanel = createStatsPanel(container, mapOverlay.mapButtonsContainer, statsCbs);
    },

    unmount(): void {
      settingsPanel?.destroy();
      tutorialOverlay?.destroy();
      summaryOverlay?.destroy();
      skillsPanel?.destroy();
      statsPanel?.destroy();
      mapOverlay?.destroy();
      editorPanel?.destroy();
      battleMenu?.destroy();

      if (container && container.parentElement) {
        container.parentElement.removeChild(container);
      }
      container = null;
      battleWrap = null;
      topBar = null;
      leftStrip = null;
      rightColumn = null;
      waveCards = null;
      settingsPanel = null;
      tutorialOverlay = null;
      summaryOverlay = null;
      skillsPanel = null;
      statsPanel = null;
      mapOverlay = null;
      editorPanel = null;
      battleMenu = null;
    },

    update(state: GameStateView): void {
      if (!container) return;

      const isBattle = state.scene === "game";
      const isMap = state.scene === "map";

      // Top bar visible on both battle and map
      if (isBattle || isMap) topBar?.update(state);

      if (battleWrap) battleWrap.style.display = isBattle ? "" : "none";
      if (isBattle) {
        leftStrip?.update(state);
        rightColumn?.update(state);
        waveCards?.update(state);
      }

      // Phase 3 overlays manage their own visibility per scene.
      settingsPanel?.update(state);
      tutorialOverlay?.update(state);
      summaryOverlay?.update(state);
      mapOverlay?.update(state);
      skillsPanel?.update(state);
      statsPanel?.update(state);
      editorPanel?.update(state);
      battleMenu?.update(state);
    },
  };
}
